import { Component, Input, ChangeDetectionStrategy, inject, computed } from '@angular/core';
import { BrandService } from 'src/app/core/services/brand.service';

/** What the logo slot should paint. `tefter` is the built-in SVG wordmark. */
export type BrandMark =
  | { kind: 'tefter' }
  | { kind: 'image'; url: string }
  | { kind: 'text'; label: string }
  | { kind: 'none' };

/**
 * Decides the mark for the current brand, fail-closed.
 *
 * A white-label brand NEVER falls through to the Tefter wordmark: the host
 * belongs to a customer, and asserting our brand on it is the failure mode PR
 * #473 closed for unresolved brands. A resolved brand with no usable logo shows
 * its own name, or nothing at all — never someone else's identity.
 */
export function resolveBrandMark(isWhiteLabelMode: boolean, logoUrl: string, appName: string): BrandMark {
  if (!isWhiteLabelMode) return { kind: 'tefter' };
  if (logoUrl) return { kind: 'image', url: logoUrl };
  if (appName) return { kind: 'text', label: appName };
  return { kind: 'none' };
}

/**
 * Whether the brand-identity name line should render beneath the mark.
 *
 * Opt-in (`showBrandName`) AND only for the `image` mark: the `tefter` mark
 * already carries "TEFTER" in its SVG, the `text` mark already renders the
 * brand name AS the mark, and `none` means we don't have a name to assert.
 * Rendering it unconditionally on `image` would double the brand name for
 * the `text` case.
 */
export function shouldShowBrandName(mark: BrandMark, showBrandName: boolean): boolean {
  return showBrandName && mark.kind === 'image';
}

@Component({
  selector: 'app-tefter-logo',
  standalone: true,
  imports: [],
  templateUrl: './tefter-logo.component.html',
  styleUrl: './tefter-logo.component.sass',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TefterLogoComponent {
  private brandService = inject(BrandService);

  readonly mark = computed(() =>
    resolveBrandMark(this.brandService.isWhiteLabelMode(), this.brandService.logoUrl(), this.brandService.appName())
  );

  readonly appName = computed(() => this.brandService.appName());

  @Input() compact = false;

  /**
   * Opt-in: render the brand name as an identity line beneath the mark, when
   * the mark is a logo `image`. See `shouldShowBrandName`. Defaults to false
   * so consumers that don't need it (e.g. `home`, `email-link-callback`) are
   * unaffected.
   */
  @Input() showBrandName = false;

  get showsBrandName(): boolean {
    return shouldShowBrandName(this.mark(), this.showBrandName);
  }

  get markSize(): number {
    return this.compact ? 30 : 38;
  }

  get svgWidth(): number {
    return this.markSize * 1.66;
  }

  get svgHeight(): number {
    return this.markSize;
  }
}
